import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useVenues } from '@/modules/venues/venues';
import {
  AUDIT_ACTIONS,
  AUDIT_TARGET_TYPES,
  useAudit,
  type AuditEvent,
  type AuditFilters,
} from './audit';

const PAGE_SIZE = 50;

type DraftFilters = {
  actor: string;
  actions: string[];
  targetType: string;
  target: string;
  venueId: string;
  from: string;
  to: string;
};

const EMPTY_DRAFT: DraftFilters = {
  actor: '',
  actions: [],
  targetType: '',
  target: '',
  venueId: '',
  from: '',
  to: '',
};

/** Converts a `datetime-local` value into an ISO-8601 instant, or undefined when blank. */
function toInstant(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function draftToFilters(draft: DraftFilters, page: number): AuditFilters {
  return {
    actor: draft.actor.trim() || undefined,
    actions: draft.actions.length ? draft.actions : undefined,
    targetType: draft.targetType || undefined,
    target: draft.target.trim() || undefined,
    venueId: draft.venueId.trim() || undefined,
    from: toInstant(draft.from),
    to: toInstant(draft.to),
    page,
    pageSize: PAGE_SIZE,
  };
}

export function AuditPage() {
  const { t } = useTranslation('common');
  const canView = useAuthorization().can('audit.read');

  const [draft, setDraft] = useState<DraftFilters>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<DraftFilters>(EMPTY_DRAFT);
  const [page, setPage] = useState(0);

  const filters = useMemo(() => draftToFilters(applied, page), [applied, page]);
  const { data, isLoading, isError } = useAudit(filters);
  const { data: venuesData } = useVenues();

  const actionLabels = t('audit.actionLabels', { returnObjects: true }) as Record<string, string>;
  const targetTypeLabels = t('audit.targetTypeLabels', {
    returnObjects: true,
  }) as Record<string, string>;
  const venues = useMemo(() => venuesData?.items ?? [], [venuesData?.items]);
  const venueNamesById = useMemo(
    () => new Map(venues.map((venue) => [venue.id, venue.name])),
    [venues],
  );

  if (!canView) return <Forbidden />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function apply(event: React.FormEvent) {
    event.preventDefault();
    setApplied(draft);
    setPage(0);
  }

  function reset() {
    setDraft(EMPTY_DRAFT);
    setApplied(EMPTY_DRAFT);
    setPage(0);
  }

  return (
    <section className="grid gap-6 p-6">
      <h1 className="text-2xl font-semibold">{t('audit.title')}</h1>

      <form
        onSubmit={apply}
        className="grid gap-4 rounded-md border p-4 md:grid-cols-2 lg:grid-cols-3"
      >
        <div className="grid gap-2">
          <Label htmlFor="audit-actor">{t('audit.filters.actor')}</Label>
          <Input
            id="audit-actor"
            value={draft.actor}
            onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="audit-actions">{t('audit.filters.actions')}</Label>
          <select
            id="audit-actions"
            multiple
            className="h-24 rounded-md border bg-transparent px-2 py-1 text-sm"
            value={draft.actions}
            onChange={(e) =>
              setDraft({
                ...draft,
                actions: Array.from(e.target.selectedOptions, (o) => o.value),
              })
            }
          >
            {AUDIT_ACTIONS.map((code) => (
              <option key={code} value={code}>
                {actionLabels[code] ?? code}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="audit-target-type">{t('audit.filters.targetType')}</Label>
          <select
            id="audit-target-type"
            className="h-9 rounded-md border bg-transparent px-2 text-sm"
            value={draft.targetType}
            onChange={(e) => setDraft({ ...draft, targetType: e.target.value })}
          >
            <option value="">{t('audit.filters.anyTargetType')}</option>
            {AUDIT_TARGET_TYPES.map((code) => (
              <option key={code} value={code}>
                {targetTypeLabels[code] ?? code}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="audit-target-id">{t('audit.filters.target')}</Label>
          <Input
            id="audit-target-id"
            value={draft.target}
            onChange={(e) => setDraft({ ...draft, target: e.target.value })}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="audit-venue">{t('audit.filters.venue')}</Label>
          <select
            id="audit-venue"
            className="h-9 rounded-md border bg-transparent px-2 text-sm"
            value={draft.venueId}
            onChange={(e) => setDraft({ ...draft, venueId: e.target.value })}
          >
            <option value="">{t('audit.filters.anyVenue')}</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="audit-from">{t('audit.filters.from')}</Label>
          <Input
            id="audit-from"
            type="datetime-local"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="audit-to">{t('audit.filters.to')}</Label>
          <Input
            id="audit-to"
            type="datetime-local"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button type="submit">{t('audit.filters.apply')}</Button>
          <Button type="button" variant="outline" onClick={reset}>
            {t('audit.filters.reset')}
          </Button>
        </div>
      </form>

      {isLoading && <p className="text-muted-foreground">{t('audit.loading')}</p>}
      {isError && <p className="text-destructive">{t('audit.errors.loadFailed')}</p>}

      {data && (
        <>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('audit.columns.time')}</th>
                  <th className="px-4 py-2 font-medium">{t('audit.columns.actor')}</th>
                  <th className="px-4 py-2 font-medium">{t('audit.columns.action')}</th>
                  <th className="px-4 py-2 font-medium">{t('audit.columns.target')}</th>
                  <th className="px-4 py-2 font-medium">{t('audit.columns.venue')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                      {t('audit.empty')}
                    </td>
                  </tr>
                )}
                {data.items.map((event) => (
                  <AuditRow
                    key={event.id}
                    event={event}
                    actionLabel={actionLabels[event.action] ?? event.action}
                    targetTypeLabel={targetTypeLabels[event.targetType] ?? event.targetType}
                    venueLabel={
                      event.venueId ? (venueNamesById.get(event.venueId) ?? event.venueId) : null
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('audit.pagination.summary', { page: page + 1, totalPages, total: data.total })}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t('audit.pagination.prev')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('audit.pagination.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function AuditRow({
  event,
  actionLabel,
  targetTypeLabel,
  venueLabel,
}: {
  event: AuditEvent;
  actionLabel: string;
  targetTypeLabel: string;
  venueLabel: string | null;
}) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className="border-b last:border-0">
        <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
          {new Date(event.occurredAt).toLocaleString()}
        </td>
        <td className="px-4 py-2">{event.actorLabel ?? t('audit.system')}</td>
        <td className="px-4 py-2">{actionLabel}</td>
        <td className="px-4 py-2 text-muted-foreground">
          {targetTypeLabel} · {event.targetLabel ?? event.targetId}
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{venueLabel ?? '—'}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? t('audit.row.collapse') : t('audit.row.expand')}
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b bg-muted/30 last:border-0">
          <td colSpan={5} className="px-4 py-3">
            <div className="grid gap-3 md:grid-cols-3">
              <JsonBlock title={t('audit.payload.before')} value={event.before} />
              <JsonBlock title={t('audit.payload.after')} value={event.after} />
              <JsonBlock title={t('audit.payload.metadata')} value={event.metadata} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  const { t } = useTranslation('common');
  return (
    <div className="grid gap-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {value == null ? (
        <p className="text-xs text-muted-foreground">{t('audit.payload.empty')}</p>
      ) : (
        <pre className="overflow-x-auto rounded-md bg-background p-2 text-xs">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}
